import apiClient, { isBackendAvailable } from './apiClient';
import type { CreateInquiryRequest, InquirySuccessResponse } from '../types';
import { validateInquiry, hasErrors } from '../utils/validation';
import { isValidApartmentId } from '../data/apartments';

class InquirySubmissionError extends Error {
  status: number;
  details: unknown;

  constructor(status: number, message: string, details: unknown = []) {
    super(message);
    this.name = 'InquirySubmissionError';
    this.status = status;
    this.details = details;
  }
}

/**
 * Submit inquiry via backend API (sends email + saves JSON file on server).
 * Falls back to local storage if backend is unreachable.
 */
export async function submitInquiry(inquiry: CreateInquiryRequest): Promise<InquirySuccessResponse> {
  // Validate locally first
  const validationErrors = validateInquiry(inquiry);
  if (hasErrors(validationErrors)) {
    throw new InquirySubmissionError(400, 'Invalid inquiry data', validationErrors);
  }

  if (!isValidApartmentId(inquiry.apartmentId)) {
    throw new InquirySubmissionError(
      404,
      `Apartment with ID ${inquiry.apartmentId} not found`,
      { apartmentId: 'validation.apartmentNotFound' },
    );
  }

  const available = await isBackendAvailable();

  if (!available) {
    // Fallback: save locally when backend is down
    storeInquiryLocally(inquiry);
    return {
      message: 'Inquiry saved locally. We will process it when connection is restored.',
      inquiryId: Date.now(),
      warning: 'BACKEND_UNAVAILABLE',
    };
  }

  const response = await apiClient.post('/inquiries', {
    apartmentId: inquiry.apartmentId,
    guestName: inquiry.guestName,
    guestEmail: inquiry.guestEmail,
    guestPhone: inquiry.guestPhone || null,
    checkInDate: inquiry.checkInDate,
    checkOutDate: inquiry.checkOutDate,
    numAdults: inquiry.numAdults,
    numChildren: inquiry.numChildren,
    message: inquiry.message || null,
    honeypot: inquiry.honeypot || null,
  });

  const data = response.data;

  if (!data.success) {
    throw new InquirySubmissionError(
      400,
      data.error?.message || 'Submission failed',
      data.error?.details,
    );
  }

  return {
    message: data.message || 'Inquiry sent successfully.',
    inquiryId: data.data?.id ?? Date.now(),
    warning: data.warning,
  };
}

function storeInquiryLocally(inquiry: CreateInquiryRequest): void {
  const inquiries = JSON.parse(localStorage.getItem('inquiries_archive') || '[]');
  inquiries.push({
    ...inquiry,
    id: Date.now(),
    submittedAt: new Date().toISOString(),
  });
  localStorage.setItem('inquiries_archive', JSON.stringify(inquiries));
}

/**
 * Get inquiries stored locally (fallback entries).
 */
export function getPendingInquiries(): CreateInquiryRequest[] {
  return JSON.parse(localStorage.getItem('inquiries_archive') || '[]');
}

/**
 * Clear locally stored inquiries.
 */
export function clearPendingInquiries(): void {
  localStorage.removeItem('inquiries_archive');
}
